import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getFirestore, doc, getDoc, updateDoc, collection, getDocs, query, where, limit } from 'firebase/firestore';
import { getPrintJobStatus, getShippingOptions } from '../services/printService/luluApi';
import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
import { getCloudFunctionUrl } from '../config/firebaseFunctionUrl';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { ChevronRightIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { PrinterIcon, CreditCardIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { printingRules, bindingOptions as BINDING_OPTIONS, interiorColorOptions as COLOR_OPTIONS, paperOptions as PAPER_OPTIONS, coverOptions as COVER_OPTIONS } from '../data/printingOptions';
import { getBook, updateBookPdfUrls } from '../services/bookService';
import { countries } from '../utils/countries';
import { getStates } from '../services/locationService';
import StyleModal from '../components/StyleSelector/StyleModal';
import { generateVerticalPDF, generateCoverPDF } from '../services/pdfService';
import { uploadPDF } from '../services/firebase/storage';

// Stripe Checkout handled server-side. No client Stripe.js usage here.

const PrintJobDetailsPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [job, setJob] = useState(null);
  const [error, setError] = useState(null);
  const [loadingLuluDetails, setLoadingLuluDetails] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [checkoutIntent, setCheckoutIntent] = useState(null);
  const [showReorderModal, setShowReorderModal] = useState(false);
  const [reorderForm, setReorderForm] = useState(null);
  const [shippingOptions, setShippingOptions] = useState([]);
  const [selectedShipping, setSelectedShipping] = useState(null);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [pdfState, setPdfState] = useState({ coverUrl: null, interiorUrl: null, loading: false });
  const [activeStyle, setActiveStyle] = useState(null); // 'binding' | 'interior' | 'paper' | 'cover'

  // Helpers to map between Lulu POD package id and our option ids
  const parsePodToSelections = (podId = '') => {
    try {
      const m = String(podId).match(/^(\d{4}X\d{4})(BW|FC)(PRE|STD)(PB|CW|LW)(060UC444|060UW444|080CW444)([MG])/);
      if (!m) return null;
      const [_all, _trim, colorSku, printSku, bindSku, paperSku, finishSku] = m;
      const binding = bindSku === 'PB' ? 'perfect-bound' : bindSku === 'CW' ? 'case-wrap' : bindSku === 'LW' ? 'linen-wrap' : null;
      const interior = colorSku === 'BW' ? 'standard-black' : (printSku === 'PRE' ? 'premium-color' : 'standard-color');
      const paper = paperSku === '060UC444' ? '60-cream-uncoated' : paperSku === '060UW444' ? '60-white-uncoated' : paperSku === '080CW444' ? '80-white-coated' : null;
      const cover = finishSku === 'G' ? 'glossy' : 'matte';
      return { binding, interior, paper, cover };
    } catch { return null; }
  };

  const regeneratePdfs = async () => {
    if (!job?.bookId) return;
    try {
      setPdfState((s) => ({ ...s, loading: true }));
      const book = await getBook(job.bookId);
      const bindingType = reorderForm?.printSelections?.binding === 'case-wrap' ? 'case-wrap' : 'perfect-bound';
      const coverPdf = await generateCoverPDF(book, { forLuluPrinting: true, bindingType });
      const interiorPdf = await generateVerticalPDF(book, { forLuluPrinting: true, skipCover: true, bindingType });
      const [coverUp, interiorUp] = await Promise.all([
        uploadPDF(coverPdf, job.bookId, 'cover'),
        uploadPDF(interiorPdf, job.bookId, 'verticalContent')
      ]);
      if (!coverUp?.url || !interiorUp?.url) throw new Error('Failed to upload generated PDFs');
      await updateBookPdfUrls(job.bookId, { cover: coverUp.url, verticalContent: interiorUp.url });
      setPdfState({ coverUrl: coverUp.url, interiorUrl: interiorUp.url, loading: false });
      toast.success('Book files prepared');
    } catch (e) {
      setPdfState((s) => ({ ...s, loading: false }));
      toast.error(e?.message || 'Failed to regenerate PDFs');
    }
  };

  const buildPodFromSelections = (sel) => {
    if (!sel) return null;
    const trimSku = '0600X0900';
    const colorSku = sel.interior === 'standard-black' ? 'BW' : 'FC';
    const printSku = sel.interior === 'premium-color' ? 'PRE' : 'STD';
    const bindSku = sel.binding === 'case-wrap' ? 'CW' : sel.binding === 'linen-wrap' ? 'LW' : 'PB';
    const paperSku = sel.paper === '60-cream-uncoated' ? '060UC444' : sel.paper === '60-white-uncoated' ? '060UW444' : sel.paper === '80-white-coated' ? '080CW444' : '060UW444';
    const finishSku = sel.cover === 'glossy' ? 'G' : 'M';
    return `${trimSku}${colorSku}${printSku}${bindSku}${paperSku}${finishSku}XX`; // keep trailing placeholders for linen/foil
  };
  
  // Define service fee at component level so it's available throughout the component
  const fixedMarkup = 500; // $5.00 per book markup
  
  // Determine the effective payment status based on both Firestore and Lulu data
  const getEffectiveStatus = (job) => {
    if (!job) return { name: 'UNKNOWN', message: 'No job data available' };
    
    // If we're still loading Lulu details, show loading status
    if (loadingLuluDetails) {
      return {
        name: 'LOADING',
        message: 'LOADING...'
      };
    }
    
    // Check if job is paid through any means in our app
    const isPaidInApp = job.stripePaid || 
                   job.paymentStatus === 'paid' || 
                   job.paymentStatus === 'PAID_AWAITING_FULFILLMENT' || 
                   job.customerPaymentStatus === 'paid' ||
                   job.details?.stripePaymentStatus === 'PAID';
    
    // Check if the print job is paid/in production in Lulu
    // Common Lulu production statuses: CREATED, PAYMENT_RECEIVED, IN_PRODUCTION, etc.
    const isPaidInLulu = job.details?.status?.name && 
                      ['CREATED', 'PAYMENT_RECEIVED', 'IN_PRODUCTION', 'MANUFACTURING'].includes(
                        job.details.status.name.toUpperCase());
                        
    // Check if the job is in PRODUCTION_DELAYED status specifically
    const isProductionDelayed = job.details?.status?.name && 
                            job.details.status.name.toUpperCase() === 'PRODUCTION_DELAYED';
    
    // Check if the print job has been shipped based on status
    // Only mark as shipped if the status explicitly indicates shipping
    const isShipped = job.details?.status?.name === 'SHIPPED' || 
                      job.status === 'SHIPPED';
    
    // If job is both paid and shipped, show it as completed
    if ((isPaidInApp || isPaidInLulu) && isShipped) {
      return {
        name: 'SHIPPED',
        message: 'ORDER COMPLETED - YOUR PACKAGE HAS BEEN SHIPPED'
      };
    }
    
    // If paid in our app and the status is PRODUCTION_DELAYED in Lulu
    if (isPaidInApp && isProductionDelayed) {
      return {
        name: 'PAID',
        message: 'PAID - PRINT PRODUCTION DELAYED - AWAITING PRINTER'
      };
    }
    
    // If paid in our app and also in Lulu (in production)
    if (isPaidInApp && isPaidInLulu) {
      return {
        name: 'PAID',
        message: 'PAID - ORDER SUBMITTED TO PRINTER - IN PRODUCTION'
      };
    }
    
    // If paid in our app but not yet processed in Lulu
    if (isPaidInApp) {
      return {
        name: 'PAID',
        message: 'PAYMENT RECEIVED - PREPARING YOUR ORDER'
      };
    }
    
    // For shipped jobs
    if (isShipped) {
      return {
        name: 'SHIPPED',
        message: 'ORDER COMPLETED - YOUR PACKAGE HAS BEEN SHIPPED'
      };
    }
    
    // Otherwise use the Lulu API status if available
    if (job.details?.status?.name) {
      return job.details.status;
    }
    
    // Fallback to our local status
    return {
      name: job.status || job.paymentStatus || 'UNKNOWN',
      message: 'Status information unavailable'
    };
  };

  // Function to fetch the latest Lulu status directly from the API
  const fetchLatestLuluStatus = async (printJobId) => {
    try {
      console.log('Fetching latest Lulu status for job ID:', printJobId);
      const luluStatusResult = await getPrintJobStatus(printJobId);
      return luluStatusResult.data;
    } catch (err) {
      console.error('Error fetching latest Lulu status:', err);
      return null;
    }
  };
  
  useEffect(() => {
    const fetchPrintJobDetails = async () => {
      try {
        setLoading(true);
        
        // First, try to fetch the print job document from Firestore
        const db = getFirestore();
        const jobDoc = await getDoc(doc(db, 'printJobs', jobId));
        
        if (!jobDoc.exists()) {
          throw new Error('Print job not found');
        }
        
        /**
         * @typedef {Object} PrintJob
         * @property {string} id - Document ID
         * @property {string} [printJobId] - Print job ID from Lulu API (optional)
         * @property {string} [bookId] - Book ID
         * @property {string} [status] - Status of the print job
         * @property {Object} [createdAt] - Firebase timestamp
         * @property {Object} [updatedAt] - Firebase timestamp for last update
         */
        /** @type {PrintJob} */
        const jobData = { id: jobDoc.id, ...jobDoc.data() };
        
        // Then fetch the detailed information from Lulu API using Netlify function
        const NETLIFY_FUNCTION_URL = "/.netlify/functions/lulu-print-job-details";
        // Use optional chaining to safely access properties that might not exist
        const printJobId = jobData?.printJobId || jobData.id;
        
        // Get detailed data using our Netlify function (POST method)
        const res = await fetch(NETLIFY_FUNCTION_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ printJobId })
        });
        if (!res.ok) {
          throw new Error('Failed to fetch print job details from Lulu API');
        }
        
        const detailedData = await res.json();
        
        // Set the job with the cached details first
        setJob({ 
          ...jobData, 
          details: detailedData,
          // Set an effective status property for easier access
          effectiveStatus: getEffectiveStatus({ ...jobData, details: detailedData })
        });
        
        try {
          const intentsQ = query(collection(db, 'checkoutIntents'), where('printJobId', '==', jobId), limit(1));
          const intentsSnap = await getDocs(intentsQ);
          if (!intentsSnap.empty) {
            const d = intentsSnap.docs[0];
            setCheckoutIntent({ id: d.id, ...d.data() });
          }
        } catch (ciErr) {
          console.warn('Unable to load checkout intent for job:', jobId, ciErr);
        }
        setLoading(false);
        
        // Now fetch the latest status directly from Lulu API
        setLoadingLuluDetails(true);
        const latestLuluData = await fetchLatestLuluStatus(printJobId);
        if (latestLuluData) {
          // Update with the freshest data
          setJob(prevJob => {
            const updatedJob = { 
              ...prevJob, 
              details: {
                ...prevJob.details,
                ...latestLuluData,
                status: latestLuluData.status || prevJob.details.status
              }
            };
            return {
              ...updatedJob,
              effectiveStatus: getEffectiveStatus(updatedJob)
            };
          });
        }
        setLoadingLuluDetails(false);
      } catch (err) {
        console.error('Error fetching print job details:', err);
        setError(err.message);
        setLoading(false);
        setLoadingLuluDetails(false);
      }
    };
    
    fetchPrintJobDetails();
    
    // Set up an interval to refresh the Lulu status every 30 seconds if the page stays open
    const refreshInterval = setInterval(async () => {
      if (job && job.printJobId) {
        const printJobId = job.printJobId || job.id;
        const latestLuluData = await fetchLatestLuluStatus(printJobId);
        if (latestLuluData) {
          setJob(prevJob => {
            const updatedJob = { 
              ...prevJob, 
              details: {
                ...prevJob.details,
                ...latestLuluData,
                status: latestLuluData.status || prevJob.details.status
              }
            };
            return {
              ...updatedJob,
              effectiveStatus: getEffectiveStatus(updatedJob)
            };
          });
        }
      }
    }, 30000); // 30 seconds
    
    return () => clearInterval(refreshInterval);
  }, [jobId]);

  // Helper function to get status style
  const getStatusStyle = (status) => {
    if (!status) return 'bg-gray-100 text-gray-800';
    
    // Make it case insensitive
    const statusLower = status.toLowerCase();
    
    if (statusLower.includes('paid')) {
      return 'bg-green-100 text-green-800';
    } else if (statusLower.includes('fail') || statusLower.includes('error') || statusLower.includes('cancel')) {
      return 'bg-red-100 text-red-800';
    } else if (statusLower.includes('process') || statusLower.includes('ship') || statusLower.includes('print')) {
      return 'bg-blue-100 text-blue-800';
    } else if (statusLower === 'unpaid') {
      return 'bg-amber-100 text-amber-800';
    } else {
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  // Determine unit book price from package/spec
  const getUnitBookPrice = () => {
    const spec = (
      job?.details?.line_items?.[0]?.pod_package_id ||
      job?.orderDetails?.printSpecId ||
      ''
    ).toString().toUpperCase();
    if (spec.includes('FC') && spec.includes('PRE')) return 24.99; // Premium color
    return 19.99; // Default
  };

  const refetchShippingCosts = async () => {
    if (!job) return;
    try {
      const toastId = toast.loading('Refreshing cost data...', { position: 'top-right' });
      setLoadingLuluDetails(true);
      const printJobId = job.printJobId || job.id;
      const latest = await fetchLatestLuluStatus(printJobId);
      if (latest) {
        setJob(prevJob => {
          const updatedJob = {
            ...prevJob,
            details: {
              ...prevJob.details,
              ...latest,
              status: latest.status || prevJob.details.status
            }
          };
          return {
            ...updatedJob,
            effectiveStatus: getEffectiveStatus(updatedJob)
          };
        });
        const shippingVal = parseFloat(latest?.costs?.shipping_cost?.total_cost_incl_tax);
        if (!Number.isFinite(shippingVal) || shippingVal <= 0) {
          toast.update(toastId, { render: 'Shipping cost is still being processed. Please wait and try again later.', type: 'info', isLoading: false, autoClose: 4000 });
        } else {
          toast.update(toastId, { render: 'Shipping cost updated.', type: 'success', isLoading: false, autoClose: 3000 });
        }
      } else {
        const toastId2 = toast.loading('Contacting printer for updated costs...', { position: 'top-right' });
        toast.update(toastId2, { render: 'Still fetching updated costs from the printer. Please try again later.', type: 'info', isLoading: false, autoClose: 4000 });
      }
    } catch (e) {
      toast.error('Could not refresh costs. Please try again shortly.', { position: 'top-right', autoClose: 4000 });
    } finally {
      setLoadingLuluDetails(false);
    }
  };

  // Handle payment process
  const handlePayment = async () => {
    // Make sure we have job data
    if (!job || !job.details) {
      toast.error('Print job information is missing');
      return;
    }
    
    // Prevent payment if costs aren't available yet
    if (!job.details.costs) {
      toast.error('Cost information not available. Please wait for the print job to be processed.');
      return;
    }
    
    setProcessingPayment(true);
    
    try {
      // First make sure user is authenticated
      const auth = getAuth();
      if (!auth.currentUser) {
        toast.error('You must be logged in to make a payment');
        setProcessingPayment(false);
        return;
      }
      
      // Get the payment amount and details from the job
      console.log('Job details costs:', job.details.costs);
      
      if (!job.details.costs) {
        console.error('No costs available - stopping payment');
        toast.error('Cost information not available. Please wait for the print job to be processed.');
        setProcessingPayment(false);
        return;
      }
      
      const luluBasePrice = parseFloat(job.details.costs.total_cost_incl_tax) || 0;
      const currency = job.details.costs.currency?.toLowerCase() || 'usd';
      const quantity = job.details.line_items?.length > 0 ? job.details.line_items[0].quantity : 1;
      
      console.log('Book quantity:', quantity);
      console.log('Lulu base price:', luluBasePrice);
      console.log('Currency:', currency);
      
      // Calculate total with book price (no processing fee)
      const bookPrice = getUnitBookPrice() * quantity;
      const totalWithBookPrice = luluBasePrice + bookPrice;
      const amountInCents = Math.round(totalWithBookPrice * 100);
      console.log(`Processing payment for ${luluBasePrice} ${currency} + $${bookPrice} book price = ${totalWithBookPrice} ${currency}`);
      console.log(`Amount in cents: ${amountInCents}`);
      
      // Use our Firebase function to create a Stripe checkout session
      // This will properly handle the dynamic pricing based on the print job cost
      
      try {
        // Call our createStripeCheckoutSession function
        const response = await fetch(getCloudFunctionUrl('createStripeCheckoutSession'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${await auth.currentUser.getIdToken()}`
          },
          body: JSON.stringify({
            printJobId: job.id, // Use the Firestore document ID
            amount: amountInCents, // Total amount including book price in cents
            quantity: quantity, // Number of books
            fixedMarkup: 0, // No processing fee
            currency: currency,
            productName: `Print Job #${job.id}`,
            customerEmail: auth.currentUser.email,
            luluStatus: job.details.status?.name, // Current status from Lulu API
            successUrl: `${window.location.origin}/account/print-jobs/${job.id}/payment-status?success=true`,
            cancelUrl: `${window.location.origin}/account/print-jobs/${job.id}/payment-status?canceled=true`,
            unitBookPriceCents: Math.round(getUnitBookPrice() * 100)
          })
        });
        
        // Check for successful response
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(`Failed to create checkout session: ${errorData.error || 'Unknown error'}`);
        }
        
        // Get the session info from the response
        const { sessionId, url } = await response.json();
        console.log('Created Stripe checkout session:', sessionId);
        
        if (url) {
          // Redirect directly to Stripe Checkout URL returned by server
          console.log('Redirecting to Stripe checkout URL:', url);
          window.location.href = url;
        } else {
          throw new Error('Missing checkout URL');
        }
        
        toast.info('Redirecting to Stripe payment page...');
      } catch (checkoutError) {
        console.error('Checkout error:', checkoutError);
        toast.error(`Payment setup failed: ${checkoutError.message}`);
        setProcessingPayment(false);
      }
      
      // We're now handling everything through Stripe's checkout flow
      // The payment status will be updated by our webhook handler
      
    } catch (error) {
      console.error('Error setting up payment:', error);
      toast.error(`Payment setup failed: ${error.message || 'Unknown error'}`);
      setProcessingPayment(false);
      setShowPaymentModal(false);
    } finally {
      // The modal should close after opening the payment page
      setProcessingPayment(false);
      setShowPaymentModal(false);
    }
  };

  const openReorderModal = () => {
    if (!job?.details) return;
    const addr = job.details.shipping_address || {};
    const qty = job.details.line_items?.[0]?.quantity || 1;
    const podId = job.details.line_items?.[0]?.pod_package_id || job.orderDetails?.printSpecId || '';
    const parsed = parsePodToSelections(podId) || { binding: undefined, interior: undefined, paper: undefined, cover: undefined };
    setReorderForm({
      name: addr.name || '',
      phone_number: addr.phone_number || '',
      street1: addr.street1 || '',
      street2: addr.street2 || '',
      city: addr.city || '',
      state_code: addr.state_code || '',
      postcode: addr.postcode || '',
      country_code: addr.country_code || 'US',
      quantity: qty,
      printSelections: {
        binding: parsed.binding || 'perfect-bound',
        interior: parsed.interior || 'standard-color',
        paper: parsed.paper || '60-white-uncoated',
        cover: parsed.cover || 'matte'
      }
    });
    setSelectedShipping(null);
    setShippingOptions([]);
    setShowReorderModal(true);

    // Auto-load shipping options using the job's address when opening
    (async () => {
      const hasBasics = addr?.street1 && addr?.city && addr?.postcode && addr?.country_code;
      if (!hasBasics) return;
      try {
        setLoadingOptions(true);
        // Clear previous selection and list while loading
        setSelectedShipping(null);
        setShippingOptions([]);
        const opts = await getShippingOptions({
          shippingAddress: {
            street1: addr.street1,
            city: addr.city,
            state_code: addr.state_code,
            postcode: addr.postcode,
            country_code: addr.country_code,
            phone_number: addr.phone_number,
            name: addr.name,
          },
          quantity: Number(qty) || 1,
          pageCount: job?.details?.line_items?.[0]?.page_count || job?.orderDetails?.pageCount || 32,
        });
        setShippingOptions(opts || []);
        if ((opts || []).length > 0) setSelectedShipping(opts[0]);
      } catch (e) {
        console.error('Auto-load shipping options failed', e);
      } finally {
        setLoadingOptions(false);
      }
    })();

    // Load existing book PDFs
    (async () => {
      if (!job?.bookId) return;
      try {
        setPdfState((s) => ({ ...s, loading: true }));
        const book = await getBook(job.bookId);
        const cover = book?.pdfUrls?.cover || null;
        const interior = book?.pdfUrls?.verticalContent || book?.pdfUrls?.interior || null;
        setPdfState({ coverUrl: cover, interiorUrl: interior, loading: false });
      } catch {
        setPdfState((s) => ({ ...s, loading: false }));
      }
    })();
  };

  const fetchShippingOptions = async () => {
    if (!reorderForm) return;
    try {
      setLoadingOptions(true);
      // Clear selection and list while fetching
      setSelectedShipping(null);
      setShippingOptions([]);
      const opts = await getShippingOptions({
        shippingAddress: {
          street1: reorderForm.street1,
          city: reorderForm.city,
          state_code: reorderForm.state_code,
          postcode: reorderForm.postcode,
          country_code: reorderForm.country_code,
          phone_number: reorderForm.phone_number,
          name: reorderForm.name
        },
        quantity: Number(reorderForm.quantity) || 1,
        pageCount: job?.details?.line_items?.[0]?.page_count || job?.orderDetails?.pageCount || 32,
      });
      const prevSelectedId = selectedShipping?.id || null;
      setShippingOptions(opts || []);
      if ((opts || []).length > 0) {
        const matched = prevSelectedId ? (opts || []).find(o => o.id === prevSelectedId) : null;
        setSelectedShipping(matched || opts[0]);
      }
    } catch (e) {
      toast.error('Could not load shipping options');
    } finally {
      setLoadingOptions(false);
    }
  };

  // Auto-refetch shipping options when quantity changes (if address is complete)
  useEffect(() => {
    if (!reorderForm) return;
    const addressReady = reorderForm.street1 && reorderForm.city && reorderForm.postcode && reorderForm.country_code && (reorderForm.country_code !== 'US' || reorderForm.state_code);
    if (!addressReady) return;
    // Avoid zero/invalid quantities
    if (!(Number(reorderForm.quantity) > 0)) return;
    fetchShippingOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reorderForm?.quantity]);

  useEffect(() => {
    if (!reorderForm) return;
    const addressReady = reorderForm.street1 && reorderForm.city && reorderForm.postcode && reorderForm.country_code && (reorderForm.country_code !== 'US' || reorderForm.state_code);
    if (!addressReady) return;
    if (!(Number(reorderForm.quantity) > 0)) return;
    fetchShippingOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reorderForm?.country_code, reorderForm?.state_code]);

  const computeReorderAmount = () => {
    if (!job?.details) return null;
    const quantity = Number(reorderForm?.quantity) || (job.details.line_items?.[0]?.quantity || 1);
    const unit = (() => {
      const interior = reorderForm?.printSelections?.interior;
      if (interior === 'premium-color') return 24.99;
      if (interior) return 19.99;
      return getUnitBookPrice();
    })();
    const totalOld = parseFloat(job.details.costs?.total_cost_incl_tax) || 0;
    const oldShip = parseFloat(job.details.costs?.shipping_cost?.total_cost_incl_tax) || 0;
    const basePrint = Math.max(totalOld - oldShip, 0);
    const newShip = selectedShipping ? parseFloat(selectedShipping.cost_excl_tax) || 0 : oldShip;
    const luluPortion = basePrint + newShip;
    const total = luluPortion + unit * quantity;
    return { total, luluPortion, unit, quantity };
  };

  const handleReorderCheckout = async () => {
    if (!job?.details || !reorderForm) return;
    const calc = computeReorderAmount();
    if (!calc) return;
    try {
      setProcessingPayment(true);
      const auth = getAuth();
      if (!auth.currentUser) {
        toast.error('You must be logged in');
        setProcessingPayment(false);
        return;
      }
      const currency = job.details.costs?.currency?.toLowerCase() || 'usd';
      const amountInCents = Math.round(calc.total * 100);
      const spec = buildPodFromSelections(reorderForm.printSelections) || job.details.line_items?.[0]?.pod_package_id || job.orderDetails?.printSpecId;
      const orderData = {
        bookId: job.bookId || null,
        email: auth.currentUser.email,
        title: job.details.line_items?.[0]?.title || job.orderDetails?.title || 'Book',
        quantity: calc.quantity,
        printSpecId: spec,
        shippingAddress: {
          name: reorderForm.name,
          phone_number: reorderForm.phone_number,
          street1: reorderForm.street1,
          street2: reorderForm.street2 || '',
          city: reorderForm.city,
          state_code: reorderForm.state_code,
          postcode: reorderForm.postcode,
          country_code: reorderForm.country_code
        },
        shippingLevel: selectedShipping?.level || job.details.shipping_level
      };
      const resp = await fetch(getCloudFunctionUrl('createStripeCheckoutSession'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await auth.currentUser.getIdToken()}`
        },
        body: JSON.stringify({
          amount: amountInCents,
          quantity: calc.quantity,
          fixedMarkup: 0,
          currency,
          productName: `Reorder for ${job.bookId || job.id}`,
          customerEmail: auth.currentUser.email,
          luluStatus: job.details.status?.name || null,
          orderData,
          unitBookPriceCents: Math.round(calc.unit * 100),
          successUrl: `${window.location.origin}/checkout/success?session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/account/print-jobs/${jobId}`
        })
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err?.error || 'Failed to start checkout');
      }
      const { url } = await resp.json();
      if (url) {
        window.location.href = url;
      } else {
        toast.error('Missing checkout URL');
        setProcessingPayment(false);
      }
    } catch (e) {
      toast.error(e.message || 'Could not start checkout');
      setProcessingPayment(false);
    }
  };

  return (
    <div>
      {/* Breadcrumbs */}
      <nav className="flex mb-6" aria-label="Breadcrumb">
        <ol className="flex items-center space-x-1 md:space-x-3">
          <li>
            <Link to="/account/print-jobs" className="flex items-center text-sm text-gray-600 hover:text-gray-900">
              <PrinterIcon className="h-4 w-4 mr-1" />
              Print Jobs
            </Link>
          </li>
          <li className="flex items-center">
            <ChevronRightIcon className="h-4 w-4 text-gray-400" />
            <span className="ml-1 text-sm font-medium text-gray-500 md:ml-2">
              Job Details
            </span>
          </li>
        </ol>
      </nav>

      {/* Page Title */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Print Job Details</h1>
        <p className="mt-1 text-sm text-gray-500">
          Detailed information about print job #{jobId}
        </p>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-lg w-full mx-4 overflow-hidden shadow-xl transform transition-all">
            <div className="px-6 pt-4 pb-2 flex justify-between items-center border-b">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <ExclamationTriangleIcon className="h-5 w-5 text-amber-500 mr-2" />
                Payment Information
              </h3>
              <button
                onClick={() => setShowPaymentModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <div className="px-6 py-4">
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-3">
                  Your print job has been accepted and is waiting for payment. Please note that once payment is made, the order will be processed immediately.
                </p>
                <div className="rounded-md bg-amber-50 p-4 mb-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <ExclamationTriangleIcon className="h-5 w-5 text-amber-400" aria-hidden="true" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-amber-800">Important Notice</h3>
                      <div className="mt-2 text-sm text-amber-700">
                        <p>Cancellation of orders is not available after payment. Please ensure all details are correct before proceeding.</p>
                      </div>
                    </div>
                  </div>
                </div>
                {job?.details?.costs && (
                  <div className="border-t border-b border-gray-200 py-4 my-4">
                    <h4 className="font-medium mb-2">Order Summary</h4>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm text-gray-600">Printing and Shipping:</span>
                      <span className="text-sm font-medium">{job.details.costs.total_cost_incl_tax} {job.details.costs.currency}</span>
                    </div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm text-gray-600">Book Price (${getUnitBookPrice().toFixed(2)} × {job.details.line_items?.[0]?.quantity || 1}):</span>
                      <span className="text-sm font-medium">{(getUnitBookPrice() * (job.details.line_items?.[0]?.quantity || 1)).toFixed(2)} {job.details.costs.currency}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-gray-200 mt-2">
                      <span className="font-medium">Total with Book Price:</span>
                      <span className="font-medium">{(parseFloat(job.details.costs.total_cost_incl_tax) + (getUnitBookPrice() * (job.details.line_items?.[0]?.quantity || 1))).toFixed(2)} {job.details.costs.currency}</span>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex justify-end">
                <button
                  onClick={() => setShowPaymentModal(false)}
                  className="mr-3 px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePayment}
                  disabled={processingPayment || !job?.details?.costs}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-indigo-400 disabled:cursor-not-allowed"
                >
                  {processingPayment ? (
                    <>
                      <svg className="-ml-1 mr-2 h-4 w-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCardIcon className="-ml-1 mr-2 h-4 w-4" aria-hidden="true" />
                      Proceed to Payment
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 text-red-700">
          {error}
        </div>
      ) : job && job.details ? (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          {showReorderModal && reorderForm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg w-full max-w-3xl mx-4 overflow-hidden shadow-xl">
                <div className="px-6 pt-4 pb-2 flex justify-between items-center border-b">
                  <h3 className="text-lg font-medium text-gray-900">Reorder Details</h3>
                  <button onClick={() => setShowReorderModal(false)} className="text-gray-400 hover:text-gray-500">
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
                <div className="px-6 py-4 grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                    <input value={reorderForm.name} onChange={e=>setReorderForm({...reorderForm,name:e.target.value})} className="w-full border rounded-md px-3 py-2" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                    <input value={reorderForm.phone_number} onChange={e=>setReorderForm({...reorderForm,phone_number:e.target.value})} className="w-full border rounded-md px-3 py-2" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
                    <input value={reorderForm.street1} onChange={e=>setReorderForm({...reorderForm,street1:e.target.value})} className="w-full border rounded-md px-3 py-2" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Apartment, suite, etc.</label>
                    <input value={reorderForm.street2} onChange={e=>setReorderForm({...reorderForm,street2:e.target.value})} className="w-full border rounded-md px-3 py-2" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                    <input value={reorderForm.city} onChange={e=>setReorderForm({...reorderForm,city:e.target.value})} className="w-full border rounded-md px-3 py-2" />
                  </div>
                  {(() => { const opts = getStates(reorderForm.country_code || ''); return opts.length > 0 ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                      <select value={reorderForm.state_code || ''} onChange={e=>setReorderForm({...reorderForm,state_code:e.target.value})} className="w-full border rounded-md px-3 py-2 bg-white">
                        <option value="">Select a state</option>
                        {opts.map(s=> <option key={s.code} value={s.code}>{s.name}</option>)}
                      </select>
                    </div>
                  ) : <div></div>; })()}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Postal Code</label>
                    <input value={reorderForm.postcode} onChange={e=>setReorderForm({...reorderForm,postcode:e.target.value})} className="w-full border rounded-md px-3 py-2" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                    <select value={reorderForm.country_code || ''} onChange={e=>setReorderForm({...reorderForm,country_code:e.target.value,state_code:''})} className="w-full border rounded-md px-3 py-2 bg-white">
                      {countries.map(c=> <option key={c.code} value={c.code}>{c.name}</option>)}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Print Options</h4>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-stretch">
                      {/* Binding summary card */}
                      <div className="h-full">
                        {(() => { const sel = BINDING_OPTIONS.find(o=>o.id===reorderForm.printSelections?.binding); return (
                          <button type="button" onClick={()=>setActiveStyle('binding')} aria-label="Select binding" className="w-full h-full min-h-[200px] flex flex-col rounded-lg border border-gray-200 bg-white p-3 text-left transition hover:shadow-sm hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500">
                            <div className="text-xs font-medium text-gray-600 mb-2">Binding Type</div>
                            {sel ? (
                              <div className="flex flex-col items-center text-center gap-2">
                                <img src={sel.image} alt="" className="w-28 h-20 object-cover rounded border border-gray-300 shadow-sm" />
                                <div className="text-sm font-semibold text-gray-900">{sel.title}</div>
                                <div className="text-xs text-gray-500">{sel.description}</div>
                              </div>
                            ) : (<div className="text-sm text-gray-500">Select binding</div>)}
                          </button>
                        ); })()}
                      </div>

                      {/* Interior summary card */}
                      <div className="h-full">
                        {(() => { const sel = COLOR_OPTIONS.find(o=>o.id===reorderForm.printSelections?.interior); return (
                          <button type="button" onClick={()=>setActiveStyle('interior')} aria-label="Select interior color" className="w-full h-full min-h-[200px] flex flex-col rounded-lg border border-gray-200 bg-white p-3 text-left transition hover:shadow-sm hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500">
                            <div className="text-xs font-medium text-gray-600 mb-2">Interior Color</div>
                            {sel ? (
                              <div className="flex flex-col items-center text-center gap-2">
                                <img src={sel.image} alt="" className="w-28 h-20 object-cover rounded border border-gray-300 shadow-sm" />
                                <div className="text-sm font-semibold text-gray-900">{sel.title}</div>
                                <div className="text-xs text-gray-500">{sel.description}</div>
                              </div>
                            ) : (<div className="text-sm text-gray-500">Select interior</div>)}
                          </button>
                        ); })()}
                      </div>

                      {/* Paper summary card */}
                      <div className="h-full">
                        {(() => { const sel = PAPER_OPTIONS.find(o=>o.id===reorderForm.printSelections?.paper); return (
                          <button type="button" onClick={()=>setActiveStyle('paper')} aria-label="Select paper type" className="w-full h-full min-h-[200px] flex flex-col rounded-lg border border-gray-200 bg-white p-3 text-left transition hover:shadow-sm hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500">
                            <div className="text-xs font-medium text-gray-600 mb-2">Paper Type</div>
                            {sel ? (
                              <div className="flex flex-col items-center text-center gap-2">
                                <img src={sel.image} alt="" className="w-28 h-20 object-cover rounded border border-gray-300 shadow-sm" />
                                <div className="text-sm font-semibold text-gray-900">{sel.title}</div>
                                <div className="text-xs text-gray-500">{sel.description}</div>
                              </div>
                            ) : (<div className="text-sm text-gray-500">Select paper</div>)}
                          </button>
                        ); })()}
                      </div>

                      {/* Cover summary card */}
                      <div className="h-full">
                        {(() => { const sel = COVER_OPTIONS.find(o=>o.id===reorderForm.printSelections?.cover); return (
                          <button type="button" onClick={()=>setActiveStyle('cover')} aria-label="Select cover finish" className="w-full h-full min-h-[200px] flex flex-col rounded-lg border border-gray-200 bg-white p-3 text-left transition hover:shadow-sm hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500">
                            <div className="text-xs font-medium text-gray-600 mb-2">Cover Finish</div>
                            {sel ? (
                              <div className="flex flex-col items-center text-center gap-2">
                                <img src={sel.image} alt="" className="w-28 h-20 object-cover rounded border border-gray-300 shadow-sm" />
                                <div className="text-sm font-semibold text-gray-900">{sel.title}</div>
                                <div className="text-xs text-gray-500">{sel.description}</div>
                              </div>
                            ) : (<div className="text-sm text-gray-500">Select cover</div>)}
                          </button>
                        ); })()}
                      </div>
                    </div>

                    {/* Style selection modals */}
                    <StyleModal
                      isOpen={activeStyle==='binding'}
                      onClose={()=>setActiveStyle(null)}
                      title="Select Binding Type"
                      description="Choose how your book will be bound"
                      options={BINDING_OPTIONS}
                      selectedOption={BINDING_OPTIONS.find(o=>o.id===reorderForm.printSelections?.binding)}
                      onSelect={(opt)=>setReorderForm(r=>({...r, printSelections:{...r.printSelections, binding: opt.id, paper: undefined, cover: undefined}}))}
                    />

                    <StyleModal
                      isOpen={activeStyle==='interior'}
                      onClose={()=>setActiveStyle(null)}
                      title="Select Interior Color"
                      description="Choose your book's interior printing style"
                      options={COLOR_OPTIONS}
                      selectedOption={COLOR_OPTIONS.find(o=>o.id===reorderForm.printSelections?.interior)}
                      onSelect={(opt)=>setReorderForm(r=>({...r, printSelections:{...r.printSelections, interior: opt.id, paper: undefined}}))}
                    />

                    <StyleModal
                      isOpen={activeStyle==='paper'}
                      onClose={()=>setActiveStyle(null)}
                      title="Select Paper Type"
                      description="Choose your book's paper quality"
                      options={(()=>{
                        const interior = reorderForm.printSelections?.interior;
                        const allowed = interior ? printingRules.compatibility.paperByColor[interior] : null;
                        return allowed ? PAPER_OPTIONS.filter(p=>allowed.includes(p.id)) : PAPER_OPTIONS;
                      })()}
                      selectedOption={PAPER_OPTIONS.find(o=>o.id===reorderForm.printSelections?.paper)}
                      onSelect={(opt)=>setReorderForm(r=>({...r, printSelections:{...r.printSelections, paper: opt.id}}))}
                    />

                    <StyleModal
                      isOpen={activeStyle==='cover'}
                      onClose={()=>setActiveStyle(null)}
                      title="Select Cover Finish"
                      description="Choose your book's cover finish"
                      options={(()=>{
                        const binding = reorderForm.printSelections?.binding;
                        const allowed = binding ? printingRules.compatibility.coverByBinding[binding] : null;
                        return allowed ? COVER_OPTIONS.filter(c=>allowed.includes(c.id)) : COVER_OPTIONS;
                      })()}
                      selectedOption={COVER_OPTIONS.find(o=>o.id===reorderForm.printSelections?.cover)}
                      onSelect={(opt)=>setReorderForm(r=>({...r, printSelections:{...r.printSelections, cover: opt.id}}))}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-medium text-gray-700">Your Book Files</h4>
                      <button onClick={regeneratePdfs} disabled={pdfState.loading} className="px-3 py-1 text-sm rounded-md border bg-white hover:bg-gray-50">
                        {pdfState.loading ? 'Preparing…' : 'Regenerate PDFs'}
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <a className="inline-flex items-center px-3 py-2 border rounded-md text-sm text-blue-600 hover:underline disabled:text-gray-400" href={pdfState.coverUrl || '#'} target="_blank" rel="noreferrer" onClick={(e)=>{ if(!pdfState.coverUrl) e.preventDefault(); }}>
                        Cover PDF
                      </a>
                      <a className="inline-flex items-center px-3 py-2 border rounded-md text-sm text-blue-600 hover:underline disabled:text-gray-400" href={pdfState.interiorUrl || '#'} target="_blank" rel="noreferrer" onClick={(e)=>{ if(!pdfState.interiorUrl) e.preventDefault(); }}>
                        Interior PDF
                      </a>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                    <input type="number" min="1" value={reorderForm.quantity} onChange={e=>setReorderForm({...reorderForm,quantity:e.target.value})} className="w-full border rounded-md px-3 py-2" />
                  </div>
                  <div className="md:col-span-2">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-medium text-gray-700">Shipping Method</h4>
                      <button onClick={fetchShippingOptions} disabled={loadingOptions} className="px-3 py-1 text-sm rounded-md border bg-white hover:bg-gray-50">
                        {loadingOptions ? 'Loading…' : 'Get shipping options'}
                      </button>
                    </div>
                    {loadingOptions ? (
                      <div className="text-sm text-gray-500 border rounded-md p-3 bg-gray-50">Fetching shipping options…</div>
                    ) : shippingOptions.length === 0 ? (
                      <div className="text-sm text-gray-500">No options loaded.</div>
                    ) : (
                      <div className="space-y-2 max-h-56 overflow-auto pr-1">
                        {shippingOptions.map(opt => (
                          <label key={opt.id} className="flex items-start gap-3 p-2 border rounded-md cursor-pointer hover:bg-gray-50">
                            <input type="radio" name="ship" checked={selectedShipping?.id===opt.id} onChange={()=>setSelectedShipping(opt)} />
                            <div className="flex-1">
                              <div className="flex justify-between items-start">
                                <div>
                                  <span className="block text-sm font-medium text-gray-900">
                                    {opt.title || opt.carrier_service_name || 'Shipping Option'}
                                  </span>
                                  <span className="block text-xs text-gray-500 mt-1">
                                    Delivery: {opt.minDeliveryDate || opt.min_delivery_date || '-'} - {opt.maxDeliveryDate || opt.max_delivery_date || '-'}
                                  </span>
                                  <span className="block text-xs text-gray-500">
                                    Transit time: {opt.transitTime || opt.transit_time || '-'} {(opt.transitTime || opt.transit_time) === 1 ? 'day' : 'days'}
                                  </span>
                                </div>
                                <span className="text-sm font-medium text-gray-900">
                                  {typeof opt.cost_excl_tax === 'number' ? `$${opt.cost_excl_tax.toFixed(2)}` : (typeof opt.cost === 'number' ? `$${opt.cost.toFixed(2)}` : 'Price unavailable')}
                                </span>
                              </div>
                              {(opt.isHomeOnly || opt.home_only || opt.isBusinessOnly || opt.business_only || opt.isPostboxOk || opt.postbox_ok) && (
                                <div className="mt-2 flex gap-2">
                                  {(opt.isHomeOnly || opt.home_only) && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">Residential Only</span>
                                  )}
                                  {(opt.isBusinessOnly || opt.business_only) && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">Business Only</span>
                                  )}
                                  {(opt.isPostboxOk || opt.postbox_ok) && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">PO Box OK</span>
                                  )}
                                </div>
                              )}
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="px-6 pb-4 pt-2 border-t">
                  {(() => {
                    const qty = Number(reorderForm?.quantity) || 1;
                    const unit = (() => {
                      const interior = reorderForm?.printSelections?.interior;
                      if (interior === 'premium-color') return 24.99;
                      if (interior) return 19.99;
                      return getUnitBookPrice();
                    })();
                    const subtotal = unit * qty;
                    const shipCost = parseFloat(selectedShipping?.cost_excl_tax || selectedShipping?.cost || 0) || 0;
                    const currency = (selectedShipping?.currency || job?.details?.costs?.currency || '').toString().toUpperCase();
                    // Base print cost from the original Lulu job (manufacturing cost)
                    const totalOld = parseFloat(job.details?.costs?.total_cost_incl_tax) || 0;
                    const oldShip = parseFloat(job.details?.costs?.shipping_cost?.total_cost_incl_tax) || 0;
                    const basePrint = Math.max(totalOld - oldShip, 0);
                    const printingAndShipping = basePrint + shipCost;
                    const total = subtotal + printingAndShipping;
                    return (
                      <div className="rounded-md border border-gray-200 bg-white p-4 mb-3">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="text-sm font-medium text-gray-900">Subtotal</div>
                            <div className="text-xs text-gray-500">{unit.toFixed(2)} {currency} per book</div>
                          </div>
                          <div className="text-sm font-medium text-gray-900">{subtotal.toFixed(2)} {currency}</div>
                        </div>
                        <div className="flex items-center justify-between mt-3">
                          <div className="text-sm text-gray-700">Printing & Shipping</div>
                          <div className="text-sm font-medium text-gray-900">{printingAndShipping.toFixed(2)} {currency}</div>
                        </div>
                        <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-200">
                          <div className="font-medium">Total</div>
                          <div className="font-semibold">{total.toFixed(2)} {currency}</div>
                        </div>
                      </div>
                    );
                  })()}
                  <div className="flex justify-end gap-2">
                    <button onClick={()=>setShowReorderModal(false)} className="px-4 py-2 border rounded-md bg-white hover:bg-gray-50 text-sm">Cancel</button>
                    <button
                      onClick={handleReorderCheckout}
                      disabled={loadingOptions || !selectedShipping || processingPayment}
                      aria-busy={processingPayment ? 'true' : 'false'}
                      className={`px-4 py-2 rounded-md text-sm ${loadingOptions || !selectedShipping || processingPayment ? 'bg-indigo-400 cursor-not-allowed opacity-60 text-white' : 'bg-indigo-600 hover:bg-indigo-700 text-white'}`}
                    >
                      {processingPayment ? 'Processing…' : 'Proceed to payment'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* UNPAID Banner - only show if not paid according to Stripe/Firestore status */}
          {!job.stripePaid && 
           job.paymentStatus !== 'paid' && 
           job.paymentStatus !== 'PAID_AWAITING_FULFILLMENT' && 
           job.customerPaymentStatus !== 'paid' && 
           job.details?.status?.name === "UNPAID" && (
            <div className="bg-amber-50 border-y border-amber-200 px-4 py-3 flex flex-col sm:flex-row justify-between items-center">
              <div className="flex items-center mb-3 sm:mb-0">
                <ExclamationTriangleIcon className="h-5 w-5 text-amber-400 mr-2" />
                <span className="text-amber-800 font-medium">
                  {job?.details?.costs ? 
                    "This print job requires payment before processing" : 
                    "Calculating costs... Payment will be available once processing is complete"
                  }
                </span>
              </div>
              <div className="flex gap-2">
                {!job?.details?.costs && (
                  <button
                    onClick={() => window.location.reload()}
                    className="inline-flex items-center px-3 py-2 border border-amber-300 text-sm font-medium rounded-md text-amber-700 bg-amber-100 hover:bg-amber-200"
                  >
                    Refresh
                  </button>
                )}
                <button
                  onClick={() => setShowPaymentModal(true)}
                  disabled={!job?.details?.costs}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-indigo-400 disabled:cursor-not-allowed"
                >
                  <CreditCardIcon className="-ml-1 mr-2 h-4 w-4" aria-hidden="true" />
                  Pay Now
                </button>
              </div>
            </div>
          )}
          {/* Job Header */}
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Print Job {job.details.id}
                </h3>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                  Created on {formatDate(job.details.date_created)}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span 
                  className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusStyle(getEffectiveStatus(job).name)}`}
                >
                  {getEffectiveStatus(job).name}
                </span>
              </div>
            </div>
          </div>

          <div className="px-4 py-5 sm:p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Basic Information */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-lg font-semibold text-gray-700 mb-4">Basic Information</h4>
                <dl className="grid grid-cols-1 gap-y-3">
                  <div className="grid grid-cols-3 gap-4">
                    <dt className="text-sm font-medium text-gray-500">Print Job ID</dt>
                    <dd className="text-sm text-gray-900 col-span-2 font-mono">{job.details.id}</dd>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <dt className="text-sm font-medium text-gray-500">Book ID</dt>
                    <dd className="text-sm text-gray-900 col-span-2 font-mono">{job.bookId}</dd>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <dt className="text-sm font-medium text-gray-500">Status</dt>
                    <dd className="text-sm text-gray-900 col-span-2">{getEffectiveStatus(job).name}</dd>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <dt className="text-sm font-medium text-gray-500">Status Message</dt>
                    <dd className="text-sm text-gray-900 col-span-2">{getEffectiveStatus(job).message || 'N/A'}</dd>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <dt className="text-sm font-medium text-gray-500">Stripe Payment Status</dt>
                    <dd className="text-sm text-gray-900 col-span-2">
                      {job.stripePaid ? 'PAID' : 
                       job.paymentStatus === 'PAID_AWAITING_FULFILLMENT' ? 'PAID' : 
                       job.paymentStatus || 'Not Paid'}
                    </dd>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <dt className="text-sm font-medium text-gray-500">Created Date</dt>
                    <dd className="text-sm text-gray-900 col-span-2">{formatDate(job.details.date_created)}</dd>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <dt className="text-sm font-medium text-gray-500">Modified Date</dt>
                    <dd className="text-sm text-gray-900 col-span-2">{formatDate(job.details.date_modified)}</dd>
                  </div>
                </dl>
              </div>

              {/* Shipping Information */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-lg font-semibold text-gray-700 mb-4">Shipping Information</h4>
                <dl className="grid grid-cols-1 gap-y-3">
                  <div className="grid grid-cols-3 gap-4">
                    <dt className="text-sm font-medium text-gray-500">Shipping Level</dt>
                    <dd className="text-sm text-gray-900 col-span-2">{job.details.shipping_level}</dd>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <dt className="text-sm font-medium text-gray-500">Tracking ID</dt>
                    <dd className="text-sm text-gray-900 col-span-2">
                      {job.details.line_items?.[0]?.tracking_id || 'TBD'}
                    </dd>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <dt className="text-sm font-medium text-gray-500">Tracking Link</dt>
                    <dd className="text-sm text-gray-900 col-span-2">
                      {job.details.line_items?.[0]?.tracking_urls?.[0] ? (
                        <a 
                          href={job.details.line_items[0].tracking_urls[0]} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          Track Package
                        </a>
                      ) : 'TBD'}
                    </dd>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <dt className="text-sm font-medium text-gray-500">Est. Ship Date</dt>
                    <dd className="text-sm text-gray-900 col-span-2">
                      {job.details.estimated_shipping_dates?.dispatch_min ? formatDate(job.details.estimated_shipping_dates.dispatch_min) : 'TBD'}
                    </dd>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <dt className="text-sm font-medium text-gray-500">Dispatch Window</dt>
                    <dd className="text-sm text-gray-900 col-span-2">
                      {job.details.estimated_shipping_dates?.dispatch_min && job.details.estimated_shipping_dates?.dispatch_max ? 
                        `${formatDate(job.details.estimated_shipping_dates.dispatch_min)} - ${formatDate(job.details.estimated_shipping_dates.dispatch_max)}` : 'TBD'}
                    </dd>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <dt className="text-sm font-medium text-gray-500">Est. Delivery Window</dt>
                    <dd className="text-sm text-gray-900 col-span-2">
                      {job.details.estimated_shipping_dates?.arrival_min && job.details.estimated_shipping_dates?.arrival_max ? 
                        `${formatDate(job.details.estimated_shipping_dates.arrival_min)} - ${formatDate(job.details.estimated_shipping_dates.arrival_max)}` : 'TBD'}
                    </dd>
                  </div>
                  {/* Only show Production Status when Lulu status is meaningful (not PAID/UNPAID) */}
                  {job.luluStatus && !['PAID', 'UNPAID', 'paid', 'unpaid'].includes(job.luluStatus) && (
                    <div className="grid grid-cols-3 gap-4">
                      <dt className="text-sm font-medium text-gray-500">Production Status</dt>
                      <dd className="text-sm text-gray-900 col-span-2">{job.luluStatus}</dd>
                    </div>
                  )}
                  <div className="grid grid-cols-3 gap-4">
                    <dt className="text-sm font-medium text-gray-500">Carrier</dt>
                    <dd className="text-sm text-gray-900 col-span-2">
                      {job.details.line_items?.[0]?.carrier_name || 'TBD'}
                    </dd>
                  </div>
                </dl>
              </div>
              
              {/* Delivery Estimates */}
              {job.details.estimated_shipping_dates && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-lg font-semibold text-gray-700 mb-4">Delivery Estimates</h4>
                  <dl className="grid grid-cols-1 gap-y-3">
                    <div className="grid grid-cols-3 gap-4">
                      <dt className="text-sm font-medium text-gray-500">Dispatch Window</dt>
                      <dd className="text-sm text-gray-900 col-span-2">
                        {new Date(job.details.estimated_shipping_dates.dispatch_min).toLocaleDateString()} - {new Date(job.details.estimated_shipping_dates.dispatch_max).toLocaleDateString()}
                      </dd>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <dt className="text-sm font-medium text-gray-500">Arrival Window</dt>
                      <dd className="text-sm text-gray-900 col-span-2">
                        {new Date(job.details.estimated_shipping_dates.arrival_min).toLocaleDateString()} - {new Date(job.details.estimated_shipping_dates.arrival_max).toLocaleDateString()}
                      </dd>
                    </div>
                  </dl>
                </div>
              )}

              {/* Shipping Address */}
              {job.details.shipping_address && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-lg font-semibold text-gray-700 mb-4">Shipping Address</h4>
                  <dl className="grid grid-cols-1 gap-y-3">
                    <div className="grid grid-cols-3 gap-4">
                      <dt className="text-sm font-medium text-gray-500">Name</dt>
                      <dd className="text-sm text-gray-900 col-span-2">
                        {job.details.shipping_address.name}
                      </dd>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <dt className="text-sm font-medium text-gray-500">Address</dt>
                      <dd className="text-sm text-gray-900 col-span-2">
                        {job.details.shipping_address.street1}<br />
                        {job.details.shipping_address.street2 && <>{job.details.shipping_address.street2}<br /></>}
                        {job.details.shipping_address.city}, {job.details.shipping_address.state_code} {job.details.shipping_address.postcode}<br />
                        {job.details.shipping_address.country_code}
                      </dd>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <dt className="text-sm font-medium text-gray-500">Phone</dt>
                      <dd className="text-sm text-gray-900 col-span-2">
                        {job.details.shipping_address.phone_number}
                      </dd>
                    </div>
                  </dl>
                </div>
              )}
            </div>

            {/* Line Items */}
            {job.details.line_items?.length > 0 && (
              <div className="mt-6">
                <h4 className="text-lg font-semibold text-gray-700 mb-4">Line Items</h4>
                {job.details.line_items.map((item, index) => (
                  <div key={item.id || index} className="bg-gray-50 p-4 rounded-lg mb-4">
                    <div className="flex flex-col md:flex-row">
                      {item.thumbnail_url && (
                        <div className="flex-shrink-0 mr-6 mb-4 md:mb-0">
                          <img 
                            src={item.thumbnail_url} 
                            alt={`Thumbnail for ${item.title}`}
                            className="w-36 object-contain border rounded"
                          />
                        </div>
                      )}
                      <div className="flex-grow">
                        <dl className="grid grid-cols-1 md:grid-cols-2 gap-y-3 gap-x-6">
                          <div className="col-span-2">
                            <dt className="text-sm font-medium text-gray-500">Title</dt>
                            <dd className="text-lg font-medium text-gray-900">{item.title}</dd>
                          </div>
                          <div>
                            <dt className="text-sm font-medium text-gray-500">Item ID</dt>
                            <dd className="text-sm text-gray-900 font-mono">{item.id}</dd>
                          </div>
                          <div>
                            <dt className="text-sm font-medium text-gray-500">Status</dt>
                            <dd className="text-sm text-gray-900">{item.status?.name || 'N/A'}</dd>
                          </div>
                          <div>
                            <dt className="text-sm font-medium text-gray-500">Quantity</dt>
                            <dd className="text-sm text-gray-900">{item.quantity}</dd>
                          </div>
                          <div>
                            <dt className="text-sm font-medium text-gray-500">Package Type</dt>
                            <dd className="text-sm text-gray-900">{item.pod_package_id}</dd>
                          </div>
                        </dl>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Cost Information */}
            {job.details.costs && (
              <div className="mt-6 bg-gray-50 p-4 rounded-lg">
                <h4 className="text-lg font-semibold text-gray-700 mb-4">Cost Information</h4>
                <dl className="grid grid-cols-1 gap-y-3">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Subtotal</dt>
                    <dd className="text-sm text-gray-900">
                      {(Number.isFinite(parseFloat(job.details.costs?.shipping_cost?.total_cost_incl_tax)) && parseFloat(job.details.costs?.shipping_cost?.total_cost_incl_tax) > 0)
                        ? `${(getUnitBookPrice() * (job.details.line_items?.[0]?.quantity || 1)).toFixed(2)} ${job.details.costs.currency}`
                        : '--'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Shipping Cost</dt>
                    <dd className="text-sm text-gray-900">
                      {(Number.isFinite(parseFloat(job.details.costs?.shipping_cost?.total_cost_incl_tax)) && parseFloat(job.details.costs?.shipping_cost?.total_cost_incl_tax) > 0)
                        ? `${parseFloat(job.details.costs?.shipping_cost?.total_cost_incl_tax).toFixed(2)} ${job.details.costs.currency}`
                        : '--'}
                    </dd>
                  </div>
                  <div className="pt-2 border-t border-gray-200">
                    <dt className="text-sm font-medium text-gray-500">Total</dt>
                    <dd className="text-sm font-medium text-gray-900">
                      {(Number.isFinite(parseFloat(job.details.costs?.shipping_cost?.total_cost_incl_tax)) && parseFloat(job.details.costs?.shipping_cost?.total_cost_incl_tax) > 0)
                        ? `${(
                            parseFloat(job.details.costs?.shipping_cost?.total_cost_incl_tax) +
                            (getUnitBookPrice() * (job.details.line_items?.[0]?.quantity || 1))
                          ).toFixed(2)} ${job.details.costs.currency}`
                        : '--'}
                    </dd>
                  </div>
                </dl>
                {!(Number.isFinite(parseFloat(job.details.costs?.shipping_cost?.total_cost_incl_tax)) && parseFloat(job.details.costs?.shipping_cost?.total_cost_incl_tax) > 0) && (
                  <div className="mt-3 flex justify-end">
                    <button
                      onClick={refetchShippingCosts}
                      disabled={loadingLuluDetails}
                      className={`inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md ${loadingLuluDetails ? 'text-gray-400 bg-gray-100 cursor-not-allowed' : 'text-gray-700 bg-white hover:bg-gray-50'}`}
                    >
                      {loadingLuluDetails ? (
                        <>
                          <svg className="-ml-0.5 mr-2 h-4 w-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                          Refreshing...
                        </>
                      ) : 'Refresh costs'}
                    </button>
                  </div>
                )}
              </div>
            )}
            
            {checkoutIntent && (
              <div className="mt-6 bg-gray-50 p-4 rounded-lg">
                <h4 className="text-lg font-semibold text-gray-700 mb-4">Cost Snapshot (Checkout)</h4>
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-y-3 gap-x-6">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Shipping Cost (excl. tax)</dt>
                    <dd className="text-sm text-gray-900">
                      {typeof checkoutIntent?.orderData?.shippingLevel?.cost_excl_tax === 'number'
                        ? checkoutIntent.orderData.shippingLevel.cost_excl_tax.toFixed(2)
                        : (checkoutIntent?.orderData?.shippingLevel?.cost_excl_tax || 'N/A')}{' '}
                      {(checkoutIntent?.orderData?.shippingLevel?.currency || checkoutIntent?.pricing?.currency || '').toString().toUpperCase()}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Quantity (at checkout)</dt>
                    <dd className="text-sm text-gray-900">{checkoutIntent?.pricing?.quantity || job?.orderDetails?.quantity || 'N/A'}</dd>
                  </div>
                </dl>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 text-yellow-700">
          Print job information not available
        </div>
      )}

      {/* Page Footer Actions */}
      <div className="mt-6 flex items-center justify-between">
        <Link 
          to="/account/print-jobs"
          className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Back to Print Jobs
        </Link>
        <button
          onClick={openReorderModal}
          disabled={loadingLuluDetails || !job?.details}
          className="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium shadow-sm bg-indigo-600 text-white hover:bg-indigo-700 disabled:bg-indigo-400 disabled:cursor-not-allowed"
        >
          <PrinterIcon className="-ml-0.5 mr-2 h-4 w-4" aria-hidden="true" />
          Reorder
        </button>
      </div>
    </div>
  );
};

export default PrintJobDetailsPage;
